/**
 * Which raster to draw for one layer at one tile placement — the single
 * source of truth for colour tiling (brief §4) shared by the live
 * TileCanvas.vue renderer and the offscreen export renderer (tileRenderer.ts),
 * so they can never drift from each other.
 *
 * Two independent colour-tiling mechanisms exist:
 *  - Hand-drawn layers (and single-bitmap imports): one bitmap, runtime-
 *    tinted per placement class via `tintCanvas` — the original brief §4
 *    mechanism. `tools.colours[class]` supplies the target hex.
 *  - PSD-imported layers with per-colour-instance paintings
 *    (`layers.addImportedRasterLayerSet`, from psdImport.ts when the source
 *    region was exported with more than one colour instance): the artist
 *    already painted each instance's true colours in Procreate, so the
 *    matching instance's bitmap is used AS-IS — no tint, because tinting an
 *    already-correctly-coloured painting would be wrong, not just redundant.
 *
 * Split into "plan" (pure decision, no drawing) + "resolve" (does the actual
 * tint) so a caller that renders many placements per frame — TileCanvas.vue —
 * can keep caching the expensive tint step per (layer, colour) itself, while
 * a one-shot caller (tileRenderer.ts) can just resolve directly.
 */
import { classifyColour, paletteHex, type WallpaperGroupId } from '@/patterns/wallpaper';
import { tintCanvas } from '@/services/rasterTint';
import { isDarkMode, useLayersStore } from '@/stores/layers';
import { sketchClassColour, useToolsStore } from '@/stores/tools';
import type { TileTransform } from '@/patterns/types';

export type PlacementBitmapPlan =
  | { kind: 'direct'; canvas: HTMLCanvasElement }
  | { kind: 'tint'; canvas: HTMLCanvasElement; hex: string };

export function planPlacementBitmap(
  layerId: string,
  t: TileTransform,
  wpgId: WallpaperGroupId,
  colourCount: number,
  colours: readonly string[],
): PlacementBitmapPlan {
  const layers = useLayersStore();
  // Sketch layers are graphite/glow and never colour-tiled (Bug 4) — the kind,
  // not the current mode, decides this, so the live and export renderers agree.
  if (layers.getLayerKind(layerId) === 'sketch') {
    return { kind: 'direct', canvas: layers.getLayerCanvas(layerId) };
  }
  const instances = layers.getLayerInstances(layerId);
  if (instances && instances.size > 0) {
    // Colour tiling off (or this document's colour count doesn't line up with
    // the instance set, e.g. after importing a PSD exported at a different
    // count) — just show one instance flat rather than crashing or blanking.
    const cls = colourCount > 1 ? classifyColour(wpgId, colourCount, t) ?? 0 : 0;
    const fallbackInstance = Math.min(...instances.keys());
    const canvas =
      instances.get(cls + 1) ?? instances.get(fallbackInstance) ?? layers.getLayerCanvas(layerId);
    return { kind: 'direct', canvas };
  }

  if (colourCount <= 1) return { kind: 'direct', canvas: layers.getLayerCanvas(layerId) };
  const cls = classifyColour(wpgId, colourCount, t);
  // null (no lattice metadata) or 0 (the main colour, already what's drawn) —
  // no tint needed either way; matches the original TileCanvas.vue check.
  if (!cls) return { kind: 'direct', canvas: layers.getLayerCanvas(layerId) };
  const hex = colours[cls] ?? colours[0]!;
  return { kind: 'tint', canvas: layers.getLayerCanvas(layerId), hex };
}

export function resolvePlacementBitmap(plan: PlacementBitmapPlan): HTMLCanvasElement {
  return plan.kind === 'direct' ? plan.canvas : tintCanvas(plan.canvas, plan.hex);
}

function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

/** One raster to blit for a layer at a placement: a cache `key`, the source
 *  `canvas`, and the `hex` to tint it to (null = draw as-is). */
export interface PlacementBlit {
  key: string;
  canvas: HTMLCanvasElement;
  hex: string | null;
}

/**
 * The full list of blits for one layer at one placement — the render source of
 * truth shared by TileCanvas.vue (cached tints) and tileRenderer.ts (one-shot).
 *
 * Authored ink (paint groups — "colour travels with the stroke, frozen at draw
 * time") returns one blit per (group, origin-class): the raster for origin-
 * class k, tinted to `paletteHex(group.paletteIndices[(j - k) mod
 * group.count], isDark)` at a placement of class j, using the GROUP's own
 * frozen swatch indices (not the live selection) resolved under the CURRENT
 * theme. So a mark keeps the ink colour in the cell it was drawn in (j = k),
 * its symmetry copies shift, and selecting a new colour never recolours it,
 * while a dark/light toggle still re-tints it correctly. Everything else
 * (sketch, imported/instance ink) collapses to a single blit via
 * planPlacementBitmap, using the live `colourCount`/`colours`.
 */
export function planLayerPlacement(
  layerId: string,
  t: TileTransform,
  wpgId: WallpaperGroupId,
  colourCount: number,
  colours: readonly string[],
): PlacementBlit[] {
  const layers = useLayersStore();

  // Sketch: colour travels with the stroke too — one count buffer per origin
  // class, each tinted (at a placement of class j) to a colour shifted by
  // (j-k). Before the artist has ever touched the colour system
  // (`tools.sketchColourActive`), that colour is the muted per-class guide
  // tone and single-colour sketch is one raw, untinted graphite blit — pixel
  // -identical to the pre-merge behaviour. Once they have, it's `colours`,
  // the same real hex palette ink already used (brief 09-layout: "coloured
  // pencil sketching is important" — merged into the one surviving tool).
  if (layers.getLayerKind(layerId) === 'sketch') {
    const tools = useToolsStore();
    const useRealColour = tools.sketchColourActive;
    const out: PlacementBlit[] = [];

    // Legacy (pre-colour-group) content — drawn before the artist engaged
    // real colour for this stroke. Untouched by anything below; still
    // live-tints to the current selection once real colour is active
    // (unchanged brief 09-layout behaviour — new strokes never land here
    // again once active, see useDrawing's colourGroupActive).
    if (colourCount <= 1) {
      out.push(
        !useRealColour
          ? { key: layerId, canvas: layers.getLayerCanvas(layerId), hex: null }
          : { key: `${layerId}:${colours[0] ?? null}`, canvas: layers.getLayerCanvas(layerId), hex: colours[0] ?? null },
      );
    } else {
      const n = colourCount;
      const j = classifyColour(wpgId, n, t);
      const dark = isDarkMode();
      for (const k of layers.sketchClassList(layerId)) {
        const canvas = layers.sketchClassCanvas(layerId, k);
        if (!canvas) continue;
        const cls = j === null ? k : mod(j - k, n);
        const hex = useRealColour ? colours[cls] ?? colours[0] ?? sketchClassColour(cls, dark) : sketchClassColour(cls, dark);
        out.push({ key: `${layerId}:sk${k}:${hex}`, canvas, hex });
      }
    }

    // Frozen colour paint groups (brief 10-colours): colour travels with the
    // stroke AND is frozen at draw time, so a later colour/symmetry change
    // never recolours already-drawn marks — each group tints with its OWN
    // frozen swatch indices, exactly like authored ink below, not the live
    // selection. Resolved to hex via the CURRENT theme, not whichever was
    // active when the stroke was drawn (brief 10-colours' dark-mode contract).
    const groups = layers.getSketchGroups(layerId);
    if (groups) {
      const dark = isDarkMode();
      groups.forEach((g, gi) => {
        const n = Math.max(1, g.count);
        const j = n > 1 ? classifyColour(wpgId, n, t) : 0;
        for (const [cls, entry] of g.classes) {
          const index = j === null ? g.paletteIndices[0]! : g.paletteIndices[mod(j - cls, n)] ?? g.paletteIndices[0]!;
          const hex = paletteHex(index, dark);
          out.push({ key: `${layerId}:sg${gi}:${cls}:${hex}`, canvas: entry.canvas, hex });
        }
      });
    }

    return out;
  }

  const groups = layers.getInkGroups(layerId);
  if (groups && groups.length > 0) {
    const out: PlacementBlit[] = [];
    const dark = isDarkMode();
    groups.forEach((g, gi) => {
      const n = Math.max(1, g.count);
      const j = n > 1 ? classifyColour(wpgId, n, t) : 0;
      for (const [cls, canvas] of g.classes) {
        const index = j === null ? g.paletteIndices[0]! : g.paletteIndices[mod(j - cls, n)] ?? g.paletteIndices[0]!;
        const hex = paletteHex(index, dark);
        out.push({ key: `${layerId}:${gi}:${cls}:${hex}`, canvas, hex });
      }
    });
    return out;
  }
  const plan = planPlacementBitmap(layerId, t, wpgId, colourCount, colours);
  return [
    plan.kind === 'direct'
      ? { key: layerId, canvas: plan.canvas, hex: null }
      : { key: `${layerId}:${plan.hex}`, canvas: plan.canvas, hex: plan.hex },
  ];
}
